import * as React from "react";

import "./spinner.css";

/**
 * Our spinner, replacing `@salt-ds/core`'s for the 58 elements across 50 files.
 *
 * ── SIZES ARE A LAYOUT CONTRACT ───────────────────────────────────────────────────────
 *
 * `medium` is exactly `--salt-size-base`, `large` is twice that, `small` is
 * `max(--salt-size-icon, 12px)`. Those are not decorative: a spinner swaps in where a button
 * label or a table cell's content was, so its box sets the height of the row around it. The
 * measurements in `spinner.css` are the vendor's, unchanged. Only the ink is ours.
 *
 * `size="default"` maps to `medium`, as it did there.
 *
 * ── role="img" IS KEPT, AND THAT WAS NOT THE FIRST ANSWER ─────────────────────────────
 *
 * This component first shipped `role="status"` on the reasoning that a status region is the
 * standard way to say "something is loading" and needs no timer. Two things in this codebase
 * say otherwise, and both were found by running the suite rather than by thinking harder:
 *
 *   1. EIGHT call sites pass `disableAnnouncer` — always beside `aria-hidden` — because the
 *      spinner sits inside a button whose label already says "Saving...". A `role="status"`
 *      spinner announces from a live region regardless of `aria-hidden` on the same node, so
 *      those eight deliberate silences would each have become a double announcement.
 *   2. `Unsubscribe.test.tsx` asserts `role="img"` by name. That is the contract, and a swap
 *      is supposed to be invisible.
 *
 * So the role is the vendor's. What changed is the announcing, below.
 *
 * ── ONE DELIBERATE CHANGE: ANNOUNCE ONCE, NOT EVERY FIVE SECONDS ──────────────────────
 *
 * The vendor re-announced the label every 5s for up to 20s. That interrupts a screen-reader
 * user mid-sentence, four times, to repeat something they were already told. This announces
 * once and then stays quiet.
 *
 * The region is rendered EMPTY and filled in an effect, which is not a stylistic choice: a
 * live region that appears with its text already in it is frequently not announced at all,
 * because assistive technology watches existing regions for CHANGES. The vendor sidestepped
 * this with one app-wide announcer mounted by its provider; mounting-then-filling gets the
 * same reliability locally, without a provider.
 *
 * `announcerInterval` and `announcerTimeout` are still accepted so no call site breaks, and
 * are deliberately inert — see the note on the props.
 */
export type SpinnerSize = "default" | "small" | "medium" | "large";

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize;
  /** Announced once when the spinner appears. Also the accessible name of the graphic. */
  "aria-label"?: string;
  /**
   * Suppresses the announcement entirely. Used at 8 call sites where the surrounding button
   * or dialog already says what is happening.
   */
  disableAnnouncer?: boolean;
  /** Announced when the spinner unmounts. Pass `null` to say nothing. */
  completionAnnouncement?: string | null;
  /**
   * Accepted and IGNORED. Both configured the vendor's repeat-announce loop, which no longer
   * exists. They are kept in the type so the call sites that pass them keep compiling, and
   * kept inert rather than deleted so removing them is a mechanical cleanup on its own,
   * instead of a silent behaviour change buried in this one.
   */
  announcerInterval?: number;
  announcerTimeout?: number;
}

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(function Spinner(
  {
    "aria-label": ariaLabel = "loading",
    announcerInterval: _announcerInterval,
    announcerTimeout: _announcerTimeout,
    className,
    completionAnnouncement = `finished ${ariaLabel}`,
    disableAnnouncer = false,
    size = "medium",
    ...rest
  },
  ref,
) {
  // The vendor accepted "default" as an alias for "medium"; keeping it means no call site has
  // to change and none silently falls through to an unstyled, zero-height box.
  const resolved = size === "default" ? "medium" : size;

  const [announcement, setAnnouncement] = React.useState("");
  React.useEffect(() => {
    if (disableAnnouncer) return;
    // Deferred to a microtask so the empty region is committed to the DOM first. Setting it
    // synchronously in the same commit is indistinguishable from rendering it filled.
    const id = requestAnimationFrame(() => setAnnouncement(ariaLabel));
    return () => {
      cancelAnimationFrame(id);
      if (completionAnnouncement) setAnnouncement(completionAnnouncement);
    };
  }, [ariaLabel, completionAnnouncement, disableAnnouncer]);

  return (
    <div
      aria-label={ariaLabel}
      className={
        className
          ? `saltSpinner saltSpinner-${resolved} ds-spinner ${className}`
          : `saltSpinner saltSpinner-${resolved} ds-spinner`
      }
      ref={ref}
      role="img"
      {...rest}
    >
      <svg aria-hidden="true" className="ds-spinner-svg" viewBox="0 0 32 32">
        <circle className="ds-spinner-track" cx="16" cy="16" r="14" />
        <circle className="ds-spinner-arc" cx="16" cy="16" r="14" />
      </svg>
      {disableAnnouncer ? null : (
        <span aria-live="polite" className="ds-visually-hidden" role="status">
          {announcement}
        </span>
      )}
    </div>
  );
});
